/**
 * The tenders module's own brand mark — "The Drop".
 *
 * A sealed envelope tilted over the open mouth of a tender box, caught the moment before it goes
 * in. תיבת המכרזים is the object the whole procedure is built around: every notice in the country
 * ends by naming a box and an hour to deposit the offer by. The tilt is the point — a bid is not
 * placed carefully, it is walked in and dropped before the deadline — and it is the only thing
 * separating this from a diagram of a box.
 *
 * WHY NOT THE HUACA PYRAMID. `HuacaMark` is the platform's mark and stays the platform's; this one
 * is the PRODUCT's, and the two co-exist rather than compete. The pyramid is a solid isometric
 * block, so this is deliberately an open line drawing: side by side they read as two different
 * things, which is what a parent brand and a module brand are.
 *
 * DIRECTIONAL, AND NEVER RTL-MIRRORED. The envelope leans left by 13°. This app is RTL throughout
 * and the instinct is to mirror anything directional — but a brand mark is not layout, and a logo
 * that flips per writing direction is two logos. The rotation is baked into the SVG rather than
 * applied in CSS precisely so no `transform` on an ancestor can be tempted to undo it.
 *
 * Flat `currentColor` with no gradient, the same contract `HuacaMark` honours, so it themes on
 * colour alone. `.huaca-mark` supplies the accent colour, inline-block and vertical alignment — the
 * class is reused rather than reimplemented, so a change to the platform's mark styling reaches
 * this one too.
 */
export function TendersMark({ size = 22, className }: { size?: number; className?: string }) {
  return (
    <svg
      className={className ? `huaca-mark ${className}` : 'huaca-mark'}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      {/* The box: an open mouth and a rim, drawn as two strokes so the envelope can sit ON the rim
          rather than behind a closed shape. */}
      <g fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2.4 13.4v4.8a2.6 2.6 0 0 0 2.6 2.6h14a2.6 2.6 0 0 0 2.6-2.6v-4.8" />
        <path d="M2 13.4h20" />
      </g>
      {/* The bid. Rotated about (12, 8) — its own centre, not the viewBox's, so the envelope pivots
          in place instead of swinging across the box. */}
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        transform="rotate(-13 12 8)"
      >
        <path d="M7.4 3h9.2a1.4 1.4 0 0 1 1.4 1.4v8.4H6V4.4A1.4 1.4 0 0 1 7.4 3Z" />
        <path d="M6.7 4.1 12 8l5.3-3.9" />
      </g>
    </svg>
  );
}

/**
 * The mark plus the product's name, for the landing masthead and footer.
 *
 * The word is מכרזים, not "huaca". The landing page is the product's only anonymous surface and it
 * carried the PLATFORM's name until now — a visitor met a brand they had never heard of, on a page
 * whose own <title> already said מכרזים. `.huaca-wordmark` is reused for the lockup's type and
 * spacing, so this stays one implementation of "a mark beside a word".
 */
export function TendersWordmark({ size = 26 }: { size?: number }) {
  return (
    <span className="huaca-wordmark">
      <TendersMark size={size} />
      מכרזים
    </span>
  );
}
